import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { Link } from "react-router-dom";
import type { SectionStatus } from "../hooks/usePageViewData";
import styles from "./SectionWrapper.module.scss";

type SectionWrapperProps = {
  number: number;
  title: string;
  status: SectionStatus;
  detailPath?: string;
  onGenerate?: () => void;
  disableGenerate?: boolean;
  showStatus?: boolean;
  lockMessage?: string;
  children: React.ReactNode;
};

export const STATUS_LABEL: Record<SectionStatus, string> = {
  generated: "生成済み",
  generating: "生成中...",
  ready: "生成可能",
  locked: "ロック中",
  stale: "更新が必要",
};

export const SectionWrapper = ({
  number,
  title,
  status,
  detailPath,
  onGenerate,
  disableGenerate,
  showStatus = true,
  lockMessage,
  children,
}: SectionWrapperProps) => {
  const isLocked = status === "locked";
  const isGenerated = status === "generated" || status === "stale";
  const showGenerateButton = status !== "generating";

  const getGenerateButtonClass = () => {
    if (status === "locked")
      return `${styles["generateButton"]} ${styles["generateButtonDisabled"]}`;
    if (status === "ready" || status === "stale")
      return `${styles["generateButton"]} ${styles["generateButtonPrimary"]}`;
    if (status === "generated")
      return `${styles["generateButton"]} ${styles["generateButtonOutline"]}`;
    return styles["generateButton"];
  };

  const getDetailButtonClass = () => {
    if (status === "generated") return `${styles["detailButton"]} ${styles["detailButtonPrimary"]}`;
    return `${styles["detailButton"]} ${styles["detailButtonOutline"]}`;
  };

  return (
    <div className={styles["container"]}>
      <div className={styles["header"]}>
        <div className={styles["headerLeft"]}>
          <span className={styles["numberBadge"]}>{number}</span>
          <span className={styles["title"]}>{title}</span>
        </div>
        <div className={styles["headerRight"]}>
          {showStatus && (
            <span className={`${styles["statusBadge"]} ${styles[`status-${status}`]}`}>
              {STATUS_LABEL[status]}
            </span>
          )}
          {showGenerateButton && onGenerate && (
            <button
              type="button"
              className={getGenerateButtonClass()}
              onClick={onGenerate}
              disabled={status === "locked" || disableGenerate}
            >
              <AutoAwesomeIcon className={styles["generateIcon"] ?? ""} />
              生成する
            </button>
          )}
          {isGenerated && detailPath && (
            <Link to={detailPath} className={getDetailButtonClass()}>
              詳細を見る
              <ArrowForwardIcon className={styles["detailIcon"] ?? ""} />
            </Link>
          )}
        </div>
      </div>
      <div className={styles["body"]}>
        {isLocked ? (
          <p className={styles["lockMessage"]}>
            {lockMessage ?? "前のドキュメントの生成を完了してください"}
          </p>
        ) : status === "ready" ? (
          <p className={styles["readyMessage"]}>要件を元にAIがドキュメントを自動生成します</p>
        ) : (
          children
        )}
      </div>
    </div>
  );
};
